import * as React from 'react'

import { Container } from '../../_shared/Container'
import { parsePressStats, PRESS_STATS_URL, type PressStats } from '../pressStats'
import bcorpLogo from '../assets/logos/bcorp.svg?url'
import styles from './Stats.module.css'

const KKBB_PROJECTS_COUNT = 30_080
const KKBB_AMOUNT_RAISED_EUR = 181_571_996

type Stat = { value: string; label: string }

const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
] as const

function formatMillions(value: number, suffix = ''): string {
  return `+${Math.floor(value / 1_000_000)}m${suffix}`
}

function formatInteger(value: number): string {
  return Math.floor(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}

function makeStats(stats: PressStats): Stat[] {
  return [
    {
      value: formatInteger(stats.projectsSuccessCount + KKBB_PROJECTS_COUNT),
      label: 'projets financés',
    },
    { value: formatMillions(stats.amountRaisedEur + KKBB_AMOUNT_RAISED_EUR, '€'), label: 'collectés' },
    { value: formatMillions(stats.usersCount), label: 'contributeur·ices' },
  ]
}

function formatDate(value: string): string {
  const date = new Date(value)
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
}

export function Stats({ initialStats }: { initialStats: PressStats | null }): React.ReactElement {
  const [pressStats, setPressStats] = React.useState(initialStats)
  const stats = pressStats === null ? null : makeStats(pressStats)

  React.useEffect(() => {
    const controller = new AbortController()

    async function refreshStats(): Promise<void> {
      try {
        const response = await fetch(PRESS_STATS_URL, { signal: controller.signal })
        if (!response.ok) return

        const updatedAt = response.headers.get('Last-Modified') ?? new Date().toISOString()
        const nextStats = parsePressStats(await response.json(), updatedAt)
        if (nextStats !== null) setPressStats(nextStats)
      } catch {
        // Keep the build-time snapshot, or no grid when prerendering also failed.
      }
    }

    void refreshStats()
    return () => controller.abort()
  }, [])

  return (
    <section className={styles.stats}>
      <Container>
        <p className={styles.stats__lede}>
          Retrouvez ici tous les éléments pour parler d'Ulule —leader du crowdfunding, fondé en 2010, B Corp depuis
          2015.
          <br />
          Ulule accompagne les créateurs et les entrepreneurs pour leur donner les meilleures chances de succès.
        </p>
        {stats !== null && (
          <>
            <div className={styles.stats__grid}>
              {stats.map((stat) => (
                <div key={stat.label} className={styles.stats__item}>
                  <div className={styles.stats__num}>{stat.value}</div>
                  <div className={styles.stats__label}>{stat.label}</div>
                </div>
              ))}
              <div className={styles.stats__item}>
                <img className={styles.stats__bcorpLogo} src={bcorpLogo} alt="Certified B Corporation" />
                <div className={styles.stats__num}>105,3</div>
                <div className={styles.stats__label}>score B Corp</div>
              </div>
            </div>
            <p className={styles.stats__updatedAt}>
              Données actualisées le <time dateTime={pressStats.updatedAt}>{formatDate(pressStats.updatedAt)}</time>
            </p>
          </>
        )}
      </Container>
    </section>
  )
}
